import { Component, OnInit, OnDestroy, ElementRef, ViewChild, AfterViewInit } from '@angular/core';

interface Node {
  element: HTMLElement;
  x: number;
  y: number;
  vx: number;
  vy: number;
}

interface Connection {
  element: HTMLElement;
  from: Node;
  to: Node;
}
@Component({
  selector: 'app-animated-background',
  imports: [],
  templateUrl: './animated-background.component.html',
  styleUrl: './animated-background.component.scss'
})
export class AnimatedBackgroundComponent {
  @ViewChild('networkContainer', { static: false }) networkContainer!: ElementRef<HTMLElement>;

  private nodes: Node[] = [];
  private connections: Connection[] = [];
  private animationId?: number;
  private particleInterval?: number;
  private depthInterval?: number;
  private isDestroyed = false;

  constructor() { }

  ngOnInit(): void { }

  ngAfterViewInit(): void {
    this.initializeNetwork();
  }

  ngOnDestroy(): void {
    this.isDestroyed = true;
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
    }
    if (this.particleInterval) {
      clearInterval(this.particleInterval);
    }
    if (this.depthInterval) {
      clearInterval(this.depthInterval);
    }
  }

  private initializeNetwork(): void {
    if (!this.networkContainer?.nativeElement) return;

    this.createNodes();
    this.createConnections();
    this.createParticles();
    this.animate();
    this.startDepthUpdates();
  }

  private createNodes(): void {
    const nodeCount = 40;
    const container = this.networkContainer.nativeElement;
    const containerRect = container.getBoundingClientRect();

    for (let i = 0; i < nodeCount; i++) {
      const node = document.createElement('div');
      node.className = 'node';

      const x = Math.random() * containerRect.width;
      const y = Math.random() * containerRect.height;

      node.style.left = x + 'px';
      node.style.top = y + 'px';
      node.style.position = 'absolute';
      node.style.animationDelay = Math.random() * 3 + 's';

      container.appendChild(node);

      this.nodes.push({
        element: node,
        x: x,
        y: y,
        vx: (Math.random() - 0.5) * 0.5,
        vy: (Math.random() - 0.5) * 0.5
      });
    }
  }

  private createConnections(): void {
    const container = this.networkContainer.nativeElement;

    this.nodes.forEach((node, i) => {
      this.nodes.forEach((otherNode, j) => {
        if (i !== j) {
          const distance = Math.sqrt(
            Math.pow(node.x - otherNode.x, 2) +
            Math.pow(node.y - otherNode.y, 2)
          );

          if (distance < 150 && Math.random() > 0.7) {
            const connection = document.createElement('div');
            connection.className = 'connection';

            const angle = Math.atan2(otherNode.y - node.y, otherNode.x - node.x);
            connection.style.left = node.x + 'px';
            connection.style.top = node.y + 'px';
            connection.style.width = distance + 'px';
            connection.style.transform = `rotate(${angle}rad)`;
            connection.style.animationDelay = Math.random() * 4 + 's';

            container.appendChild(connection);
            this.connections.push({
              element: connection,
              from: node,
              to: otherNode
            });
          }
        }
      });
    });
  }

  private createParticles(): void {
    this.particleInterval = window.setInterval(() => {
      if (this.isDestroyed) return;

      if (Math.random() > 0.8) {
        const containerRect = this.networkContainer.nativeElement.getBoundingClientRect();
        const particle = document.createElement('div');
        particle.className = 'particle';
        particle.style.left = Math.random() * containerRect.width + 'px';
        particle.style.top = containerRect.height + 'px';
        particle.style.position = 'absolute';
        particle.style.animationDelay = '0s';

        this.networkContainer.nativeElement.appendChild(particle);

        setTimeout(() => {
          if (particle.parentNode && !this.isDestroyed) {
            particle.parentNode.removeChild(particle);
          }
        }, 8000);
      }
    }, 500);
  }

  private animate(): void {
    if (this.isDestroyed) return;

    const containerRect = this.networkContainer.nativeElement.getBoundingClientRect();

    this.nodes.forEach(node => {
      node.x += node.vx;
      node.y += node.vy;

      if (node.x < 0 || node.x > containerRect.width) node.vx *= -1;
      if (node.y < 0 || node.y > containerRect.height) node.vy *= -1;

      node.element.style.left = node.x + 'px';
      node.element.style.top = node.y + 'px';
    });

    this.connections.forEach(connection => {
      const distance = Math.sqrt(
        Math.pow(connection.from.x - connection.to.x, 2) +
        Math.pow(connection.from.y - connection.to.y, 2)
      );

      const angle = Math.atan2(
        connection.to.y - connection.from.y,
        connection.to.x - connection.from.x
      );

      connection.element.style.left = connection.from.x + 'px';
      connection.element.style.top = connection.from.y + 'px';
      connection.element.style.width = distance + 'px';
      connection.element.style.transform = `rotate(${angle}rad)`;
    });

    this.animationId = requestAnimationFrame(() => this.animate());
  }

  private startDepthUpdates(): void {
    this.depthInterval = window.setInterval(() => {
      if (this.isDestroyed) return;
      this.updateCardDepth();
    }, 100);
  }

  private updateCardDepth(): void {
    const cards = this.networkContainer.nativeElement.querySelectorAll('.resume-card');
    cards.forEach((card: Element) => {
      const htmlCard = card as HTMLElement;
      const rect = htmlCard.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;

      const distanceFromCenter = Math.sqrt(
        Math.pow(centerX - window.innerWidth / 2, 2) +
        Math.pow(centerY - window.innerHeight / 2, 2)
      );

      const maxDistance = Math.sqrt(
        Math.pow(window.innerWidth / 2, 2) +
        Math.pow(window.innerHeight / 2, 2)
      );

      const opacity = 1 - (distanceFromCenter / maxDistance) * 0.7;
      htmlCard.style.opacity = Math.max(0.2, opacity).toString();
    });
  }
}
